/**
 * DV-DEP-003: Job Status Panel Component
 *
 * Displays job timeline, status, and errors for YouTube deployments.
 * Shows scheduling, progress, and allows retry on failures.
 */

import React from 'react';
import {
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Calendar,
  Youtube,
  RotateCcw,
  Ban,
  PlayCircle,
  Timer
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { formatTimeAgo } from '@/utils/format';
import { useProductionJobs, useRetryDeployment } from '@/hooks/useDeployments';
import type { DeploymentJob } from '@/types/deployments';

interface JobStatusPanelProps {
  productionId: string;
  className?: string;
}

const JobStatusPanel: React.FC<JobStatusPanelProps> = ({
  productionId,
  className,
}) => {
  const { data: jobsData, isLoading, error } = useProductionJobs(productionId);
  const retryDeployment = useRetryDeployment();

  const getJobTypeIcon = (type: DeploymentJob['type']) => {
    switch (type) {
      case 'youtube_upload':
        return <Youtube className="w-4 h-4" />;
      case 'youtube_schedule':
        return <Calendar className="w-4 h-4" />;
      default:
        return <PlayCircle className="w-4 h-4" />;
    }
  };

  const getJobStatusIcon = (status: DeploymentJob['status']) => {
    switch (status) {
      case 'queued':
        return <Timer className="w-4 h-4 text-blue-400" />;
      case 'processing':
        return <Loader2 className="w-4 h-4 text-yellow-400 animate-spin" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-400" />;
      case 'cancelled':
        return <Ban className="w-4 h-4 text-gray-400" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getJobStatusColor = (status: DeploymentJob['status']) => {
    switch (status) {
      case 'queued':
        return 'text-blue-400 bg-blue-500/10 border-blue-500/20';
      case 'processing':
        return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20';
      case 'completed':
        return 'text-green-400 bg-green-500/10 border-green-500/20';
      case 'failed':
        return 'text-red-400 bg-red-500/10 border-red-500/20';
      case 'cancelled':
        return 'text-gray-400 bg-gray-500/10 border-gray-500/20';
      default:
        return 'text-gray-400 bg-gray-500/10 border-gray-500/20';
    }
  };

  const getScheduledTimeDisplay = (job: DeploymentJob) => {
    if (job.deployment?.scheduledAt) {
      const scheduledTime = new Date(job.deployment.scheduledAt);
      const now = new Date();
      const isUpcoming = scheduledTime > now;

      return (
        <div className="flex items-center space-x-1 text-xs">
          <Calendar className="w-3 h-3" />
          <span className={isUpcoming ? 'text-purple-400' : 'text-white/60'}>
            {isUpcoming ? 'Scheduled for' : 'Was scheduled for'} {scheduledTime.toLocaleString()}
          </span>
        </div>
      );
    }
    return null;
  };

  const handleRetry = async (deploymentId: string) => {
    try {
      await retryDeployment.mutateAsync(deploymentId);
    } catch (error) {
      console.error('Retry failed:', error);
    }
  };

  if (isLoading) {
    return (
      <div className={cn('bg-black/40 backdrop-blur-sm border border-cyan-500/10 rounded-lg p-4', className)}>
        <div className="flex items-center space-x-2 mb-4">
          <Loader2 className="w-4 h-4 animate-spin text-cyan-400" />
          <span className="text-white/60 font-mono text-sm">Loading jobs...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('bg-black/40 backdrop-blur-sm border border-red-500/20 rounded-lg p-4', className)}>
        <div className="flex items-center space-x-2">
          <AlertCircle className="w-4 h-4 text-red-400" />
          <span className="text-red-400 font-mono text-sm">Failed to load jobs</span>
        </div>
      </div>
    );
  }

  const jobs = jobsData?.jobs || [];

  if (jobs.length === 0) {
    return (
      <div className={cn('bg-black/40 backdrop-blur-sm border border-cyan-500/10 rounded-lg p-4', className)}>
        <div className="text-center text-white/40 font-mono text-sm">
          No deployment jobs found
        </div>
      </div>
    );
  }

  return (
    <div className={cn('bg-black/40 backdrop-blur-sm border border-cyan-500/10 rounded-lg p-4', className)}>
      <div className="flex items-center space-x-2 mb-4">
        <Timer className="w-4 h-4 text-cyan-400" />
        <h3 className="text-cyan-400 font-mono text-sm font-medium">Deployment Timeline</h3>
        <span className="text-white/40 text-xs">({jobs.length} job{jobs.length !== 1 ? 's' : ''})</span>
      </div>

      <div className="space-y-3">
        {jobs.map((job) => (
          <div
            key={job.id}
            className="bg-black/60 border border-white/10 rounded-lg p-3 space-y-2"
          >
            {/* Job Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {getJobTypeIcon(job.type)}
                <span className="text-white font-mono text-sm">
                  {job.deployment?.title || 'YouTube Upload'}
                </span>
              </div>

              <div className="flex items-center space-x-2">
                <span className={cn(
                  'inline-flex items-center space-x-1 px-2 py-1 rounded text-xs font-mono border',
                  getJobStatusColor(job.status)
                )}>
                  {getJobStatusIcon(job.status)}
                  <span className="capitalize">{job.status}</span>
                </span>
              </div>
            </div>

            {/* Scheduled Time */}
            {getScheduledTimeDisplay(job)}

            {/* Progress Bar */}
            {job.status === 'processing' && job.progress > 0 && (
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-white/60">Progress</span>
                  <span className="text-cyan-400">{job.progress}%</span>
                </div>
                <div className="w-full bg-black/60 rounded-full h-2">
                  <div
                    className="bg-cyan-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${job.progress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Error Message */}
            {job.status === 'failed' && job.errorMessage && (
              <div className="bg-red-500/10 border border-red-500/20 rounded p-2">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <div className="space-y-1">
                    <p className="text-red-400 text-xs font-mono">{job.errorMessage}</p>
                    {job.errorCode && (
                      <p className="text-red-400/60 text-xs font-mono">Code: {job.errorCode}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Timing Information */}
            <div className="flex items-center justify-between text-xs text-white/40 font-mono">
              <div className="flex items-center space-x-4">
                <span>Created {formatTimeAgo(job.createdAt)}</span>
                {job.startedAt && (
                  <span>Started {formatTimeAgo(job.startedAt)}</span>
                )}
                {job.completedAt && (
                  <span>Completed {formatTimeAgo(job.completedAt)}</span>
                )}
              </div>

              {/* Retry Button */}
              {job.status === 'failed' && job.deployment && (
                <button
                  onClick={() => handleRetry(job.deployment!.id)}
                  disabled={retryDeployment.isPending}
                  className="flex items-center space-x-1 px-2 py-1 rounded border border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10 transition-colors disabled:opacity-50"
                >
                  {retryDeployment.isPending ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <RotateCcw className="w-3 h-3" />
                  )}
                  <span>Retry</span>
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default JobStatusPanel;